import React from 'react';
import styled from 'styled-components';

const ProfileCard = styled.div`
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid #eaeaea;
    transition: transform 0.2s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
    }
`;

const HeaderSection = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
`;

const ApiaryImage = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 16px;
    object-fit: cover;
    margin-right: 20px;
    box-shadow: 0 4px 10px rgba(255, 135, 1, 0.2);
    border: 2px solid #fff;
`;

const TitleSection = styled.div`
    flex: 1;
`;

const ApiaryName = styled.h2`
    margin: 0 0 4px 0;
    color: #2d3436;
    font-size: 24px;
    font-weight: 700;
`;

const ApiaryID = styled.p`
    margin: 0;
    color: #b2bec3;
    font-size: 14px;
    font-family: 'Monaco', monospace;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
`;

const InfoItem = styled.div`
    background: #fdfdfd;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #f5f5f5;
`;

const Label = styled.div`
    color: #636e72;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
    font-weight: 600;
`;

const Value = styled.div`
    color: #2d3436;
    font-size: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
`;

const StatusBadge = styled.span`
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    background-color: ${props => props.$active ? '#e6fffa' : '#fff5f5'};
    color: ${props => props.$active ? '#00b894' : '#d63031'};
    border: 1px solid ${props => props.$active ? '#b2f2bb' : '#ffa8a8'};
`;

const MapLink = styled.a`
    display: inline-flex;
    align-items: center;
    margin-left: 10px;
    color: #ff8701;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    background: #fff4e6;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s;

    &:hover {
        background: #ffe8cc;
        text-decoration: none;
        color: #d97200;
    }
`;

const ApiaryProfile = ({ apiary }) => {
    if (!apiary) return null;

    // Debug log to check available IDs for other services
    console.log('Apiary Full Object:', apiary);

    const isDisabled = apiary.is_active === false;
    const isActive = !isDisabled && apiary.operational_status === 'Active';

    return (
        <ProfileCard>
            <HeaderSection>
                <ApiaryImage
                    src={apiary.apiaryImage || 'https://static.vecteezy.com/system/resources/previews/026/148/953/non_2x/apiary-fill-outline-icon-design-illustration-nature-and-ecology-symbol-on-white-background-eps-10-file-vector.jpg'}
                    alt={apiary.apiary_name}
                />
                <TitleSection>
                    <ApiaryName>{apiary.apiary_name}</ApiaryName>
                    <ApiaryID>ID: {apiary.apiary_id}</ApiaryID>
                </TitleSection>
                <StatusBadge $active={isActive && !isDisabled}>
                    {isDisabled ? 'Disabled' : (apiary.operational_status || 'Unknown')}
                </StatusBadge>
            </HeaderSection>

            <InfoGrid>
                <InfoItem>
                    <Label>Supervisor & Beekeeper</Label>
                    <Value>
                        {apiary.supervisor_name || 'N/A'} <br />
                        <span style={{ fontSize: '13px', color: '#b2bec3', marginTop: '4px' }}>
                            (Beekeeper: {apiary.beekeeper_name || 'N/A'})
                        </span>
                    </Value>
                </InfoItem>

                <InfoItem>
                    <Label>Location</Label>
                    <Value>
                        {apiary.block ? `${apiary.village}, ${apiary.block}` : apiary.village || 'N/A'}
                        <div style={{ width: '100%', fontSize: '13px', color: '#636e72', marginTop: '2px' }}>
                            {apiary.district}, {apiary.state}
                        </div>
                        {(apiary.geo_location?.lat || apiary.latitude) && (apiary.geo_location?.lng || apiary.longitude) && (
                            <MapLink
                                href={`https://www.google.com/maps/search/?api=1&query=${apiary.geo_location?.lat || apiary.latitude},${apiary.geo_location?.lng || apiary.longitude}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ marginLeft: 0, marginTop: '8px' }}
                            >
                                📍 View Map
                            </MapLink>
                        )}
                    </Value>
                </InfoItem>

                <InfoItem>
                    <Label>Flora Source</Label>
                    <Value>
                        {apiary.selected_flower && apiary.selected_flower.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {apiary.selected_flower.map((flower, index) => (
                                    <span key={flower._id || index} style={{
                                        background: '#fff9db',
                                        color: '#f59f00',
                                        padding: '4px 10px',
                                        borderRadius: '16px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        border: '1px solid #ffe066'
                                    }}>
                                        {flower.localName || flower.plant}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            apiary.flora || 'N/A'
                        )}
                    </Value>
                </InfoItem>

                <InfoItem>
                    <Label>Capacity</Label>
                    <Value>
                        {apiary.apiary_type === 'Field'
                            ? `${apiary.original_area || 0} ${apiary.original_unit || ''}`
                            : `${apiary.original_units || apiary.hive_count || apiary.no_of_hives || 0} Units`
                        }
                        <span style={{ fontSize: '12px', color: '#636e72', marginLeft: '8px', fontWeight: '400' }}>
                            (Setup: {apiary.setup_date ? new Date(apiary.setup_date).toLocaleDateString() : 'N/A'})
                        </span>
                    </Value>
                </InfoItem>
            </InfoGrid>
        </ProfileCard >
    );
};

export default ApiaryProfile;
